import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiDirectChannelsDeviceBehaviorPlugin } from '@shure/device-ui/direct-channels/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind,
	SchematicsDeviceChannel
} from '@shure/device-ui/schematics/data-access';
import { DspChannelType, DspCopyChannelProperty } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { ChannelGroup, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class P600DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		DeviceUiDirectChannelsDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.P600];

	constructor(protected readonly transloco: TranslocoService) {}

	public getInputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DanteMicInput,
			ChannelGroup.DanteLineInput,
			ChannelGroup.AnalogInput,
			ChannelGroup.UsbInput
		];
	}

	public getOutputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteOutput, ChannelGroup.AnalogOutput, ChannelGroup.UsbOutput];
	}

	public getAutomixedChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteMicInput];
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return false;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.automixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.direct-channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(
				([
					schematicLabel,
					inputsLabel,
					intellimixLabel,
					directChannelsLabel,
					matrixMixerLabel,
					outputsLabel,
					presetsLabel,
					settingsLabel
				]) => [
					{
						route: DeviceUiRoute.Schematic,
						label: schematicLabel
					},
					{
						route: DeviceUiRoute.Inputs,
						label: inputsLabel
					},
					{
						route: DeviceUiRoute.Intellimix,
						label: intellimixLabel
					},
					{
						route: DeviceUiRoute.DirectChannels,
						label: directChannelsLabel
					},
					{
						route: DeviceUiRoute.Matrixmixer,
						label: matrixMixerLabel
					},
					{
						route: DeviceUiRoute.Outputs,
						label: outputsLabel
					},
					{
						route: DeviceUiRoute.Presets,
						label: presetsLabel
					},
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					}
				]
			)
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteMicInput, 0],
			[ChannelGroup.Automix, 1],
			[ChannelGroup.DanteLineInput, 2],
			[ChannelGroup.AnalogInput, 3],
			[ChannelGroup.UsbInput, 4]
		]);
	}

	public getDeviceOutputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.AecReferenceMixOutput, 0],
			[ChannelGroup.DanteOutput, 1],
			[ChannelGroup.AnalogOutput, 2],
			[ChannelGroup.UsbOutput, 3]
		]);
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['equalizer', 0],
			['compressor', 1],
			['delay', 2]
		]);
	}

	public getSchematicAutomixChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['deverb', 0],
			['denoiser', 1],
			['equalizer', 2],
			['compressor', 3]
		]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		_channelType: DspChannelType,
		_selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		return ['muted', 'gain', 'analogLevel', 'autoMute'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public getDirectChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DirectOutputDanteMicInput];
	}

	public showAutomixOutAsDirectChannel(_model: DeviceModel): boolean {
		return false;
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(): boolean {
		return true;
	}

	public hideDspButtons(_model: DeviceModel): boolean {
		return true;
	}

	public useAIAec(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDenoiser(_model: DeviceModel): boolean {
		return true;
	}

	public useAIDeverb(_model: DeviceModel): boolean {
		return true;
	}

	public enableStereoAudio(_model: DeviceModel, _numberOfChannels: number): boolean {
		return true;
	}

	// TODO - This is a temporary workaround for beta, and should be removed when stereo audio is supported in the P600.
	// Related ticket: https://shure.atlassian.net/browse/DNG-34389
	public showStereoAudioToggle(_model: DeviceModel): boolean {
		return false;
	}

	public showUSBStereoAudioMessage(_model: DeviceModel): boolean {
		return true;
	}

	/**
	 * Channel groups whose names are automatically derived from their source channels.
	 * Excluded from duplicate-name validation to prevent false positives.
	 */
	public getMirroredChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.Automix,
			ChannelGroup.DirectOutput,
			ChannelGroup.DirectOutputDanteMicInput,
			ChannelGroup.DirectOutputAnalogInput
		];
	}
}
